import * as React from 'react';
import { useCallback } from 'react';
import type { FormikProps } from 'formik';
import * as Immutable from 'immutable';

import type Search from 'views/logic/search/Search';
import SearchBar from 'data-lake/preview/types/internal/SearchBar';
import type { ActivePreviewSearch, DataLakeURLQueryParams } from 'data-lake/preview/Types';
import DataLakePreview from 'data-lake/preview/DataLakePreview';
import useInitialSearchBarFormValues from 'data-lake/preview/types/aws-security/hooks/useInitialFormValues';
import { WIDGET_DEFAULTS, MAX_LOADED_MESSAGES, EXTERNAL_DATA_LAKE_SEARCH_TYPE } from 'data-lake/preview/Constants';
import { onInitializingTimerange } from 'views/components/TimerangeForForm';
import useUserDateTime from 'hooks/useUserDateTime';
import { normalizeFromSearchBarForBackend } from 'views/logic/queries/NormalizeTimeRange';
import Query from 'views/logic/queries/Query';
import generateId from 'logic/generateId';
import type DataLakeWidgetConfig from 'data-lake/logic/DataLakeWidgetConfig';
import type DataLakeWidget from 'data-lake/logic/DataLakeWidget';
import type {
  AwsSecurityDataLakeFormValues,
  AwsSecurityDataLakeSearchType,
} from 'data-lake/preview/types/aws-security/Types';
import createInitialWidget from 'data-lake/preview/types/aws-security/createInitialWidget';
import { AWS_SECURITY_DATA_LAKE_QUERY } from 'data-lake/preview/types/aws-security/Constants';
import TableFilter from 'data-lake/preview/types/aws-security/TableFilter';

const DEFAULT_FIELDS = Immutable.OrderedSet([
  'start.time',
  'class_name',
  'actor.user.name',
  'activity_name',
  'severity',
  'metadata.product.name',
  'src_endpoint.ip',
  'dest_endpoint.ip',
  'status',
]);

const validate = (formValues: AwsSecurityDataLakeFormValues) => {
  const errors = {};

  if (!formValues.table) {
    return { ...errors, table: 'Table is required' };
  }

  return errors;
};

type AwsSecurityDataLakeSearchBarProps = {
  executedSearch: Search | undefined;
  onReset: () => void;
  onSubmit: (formValues: AwsSecurityDataLakeFormValues) => void;
  formRef?: React.RefObject<FormikProps<AwsSecurityDataLakeFormValues>>;
  searchJobIsRunning: boolean;
  initialUrlParams: DataLakeURLQueryParams;
};

const AwsSecurityDataLakeSearchBar = ({
  executedSearch,
  onReset,
  onSubmit,
  formRef = undefined,
  searchJobIsRunning,
  initialUrlParams,
}: AwsSecurityDataLakeSearchBarProps) => {
  const initialFormValues = useInitialSearchBarFormValues(executedSearch, initialUrlParams);

  return (
    <SearchBar<AwsSecurityDataLakeFormValues>
      rightColumn={<TableFilter />}
      validate={validate}
      emptyFormValues={{
        timerange: WIDGET_DEFAULTS.timerange,
        table: undefined,
        fields: undefined,
      }}
      initialValues={initialFormValues}
      onReset={onReset}
      onSubmit={onSubmit}
      formRef={formRef}
      searchJobIsRunning={searchJobIsRunning}
    />
  );
};

type Props = {
  initialSearch: Search | undefined;
  initialSearchJob: { status: ActivePreviewSearch['status']; id: string; nodeId: string; progress: number } | undefined;
  initialUrlParams: DataLakeURLQueryParams;
  resetUrlQueryParams: () => void;
};

const AwsSecurityDataLakePreview = ({
  initialSearch,
  initialSearchJob,
  initialUrlParams,
  resetUrlQueryParams,
}: Props) => {
  const { formatTime, userTimezone } = useUserDateTime();
  const renderSearchBar = useCallback(
    ({
      executedSearch,
      onReset,
      onSubmit,
      searchJobIsRunning,
      formRef,
    }: Omit<AwsSecurityDataLakeSearchBarProps, 'initialUrlParams'>) => (
      <AwsSecurityDataLakeSearchBar
        formRef={formRef}
        executedSearch={executedSearch}
        onReset={onReset}
        onSubmit={onSubmit}
        searchJobIsRunning={searchJobIsRunning}
        initialUrlParams={initialUrlParams}
      />
    ),
    [initialUrlParams],
  );

  const widgetFromFormValues = useCallback(
    (widget: DataLakeWidget, { fields, timerange, table }: AwsSecurityDataLakeFormValues) => {
      const updatedWidgetConfig: DataLakeWidgetConfig = widget.config
        .toBuilder()
        .fieldFilters(fields?.fieldFilters ? Immutable.List(fields.fieldFilters) : widget.config.fieldFilters)
        .filtersOperator(fields?.operator ?? widget.config.filtersOperator)
        .build();

      return widget
        .toBuilder()
        .config(updatedWidgetConfig)
        .timerange(timerange ? normalizeFromSearchBarForBackend(timerange, userTimezone) : widget.timerange)
        .streams([table])
        .build();
    },
    [userTimezone],
  );

  const filtersFromSearchType = useCallback(
    (searchType: AwsSecurityDataLakeSearchType) => ({
      table: searchType.table,
      timerange: onInitializingTimerange(searchType.timerange, formatTime),
      fields: {
        fieldFilters: searchType.field_filters ?? WIDGET_DEFAULTS.fieldFilters,
        operator: searchType.filters_operator ?? WIDGET_DEFAULTS.fieldsOperator,
      },
    }),
    [formatTime],
  );
  const queryFromFormValues = useCallback(
    (widget: DataLakeWidget, { table }: AwsSecurityDataLakeFormValues) =>
      Query.builder()
        .id(generateId())
        .query(AWS_SECURITY_DATA_LAKE_QUERY)
        .searchTypes([
          {
            size: MAX_LOADED_MESSAGES,
            filters_operator: widget.config.filtersOperator,
            timerange: widget.timerange,
            table,
            field_filters: widget.config.fieldFilters.toArray(),
            fields: widget.config.fields.toArray(),
            type: EXTERNAL_DATA_LAKE_SEARCH_TYPE,
            filters: [],
          } as unknown as AwsSecurityDataLakeSearchType,
        ])
        .build(),
    [],
  );

  const widgetFromSearch = useCallback((search: Search) => createInitialWidget(search), []);

  return (
    <DataLakePreview<AwsSecurityDataLakeFormValues>
      createInitialWidget={widgetFromSearch}
      defaultWidgetFields={DEFAULT_FIELDS}
      filtersFromSearchType={filtersFromSearchType}
      initialSearch={initialSearch}
      initialSearchJob={initialSearchJob}
      queryFromFormValues={queryFromFormValues}
      resetUrlQueryParams={resetUrlQueryParams}
      searchBar={renderSearchBar}
      widgetFromFormValues={widgetFromFormValues}
    />
  );
};

export default AwsSecurityDataLakePreview;
